import { useRef } from 'react';

import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import useRect from 'common/hooks/useRect';
import { FCWithChildren } from 'common/types';
import webView from 'common/webView';
import { Sidebar as RebrandedSidebar } from 'features/RebrandedSidebar';
import OldSidebar from 'features/Sidebar';

import { ContentContext, DisableBrowsingContext } from './context';
import { ContentBox, FixedWidthContainer, Grid, Screen } from './styles';
import { ContentProps } from './types';

/**
 * A Content Box used inside an AppLayout component to render content
 * with adequate spacings.
 *
 * @param {string} [layout='fluid'] - Can be either 'fluid' (ie. full-width, the default value)
 * or 'fixed-width' (a box of 600px)
 * @return {ReactElement} A ContentBox
 */
const Content: FCWithChildren<ContentProps> = ({
  children,
  layout = 'fluid',
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rect] = useRect(containerRef);
  const content =
    layout === 'fixed-width' ? (
      <FixedWidthContainer>{children}</FixedWidthContainer>
    ) : (
      children
    );

  return (
    <ContentContext.Provider value={rect}>
      <ContentBox ref={containerRef} {...props}>
        {content}
      </ContentBox>
    </ContentContext.Provider>
  );
};

const AppLayout: FCWithChildren<{ disableBrowsing?: boolean }> & {
  Content: typeof Content;
} = ({ children, disableBrowsing = false }) => {
  const { featureFlags } = useFeatureFlagsSwitcher();

  /**
   * Webview screens are intended to be used rarely and for very specific flows that are designed for it,
   * thus in those cases we don't want the sidebar or any global layout components
   */
  if (webView.isInWebView) {
    return children;
  }

  return (
    <DisableBrowsingContext.Provider value={disableBrowsing}>
      <Grid>
        {featureFlags.enableNewNav ? (
          <RebrandedSidebar />
        ) : (
          <OldSidebar disableBrowsing={disableBrowsing} />
        )}
        <Screen>{children}</Screen>
      </Grid>
    </DisableBrowsingContext.Provider>
  );
};

AppLayout.Content = Content;

export default AppLayout;
